/**
 * Simplified Auth Hook - single source of truth approach
 * All data comes directly from SimplifiedAuthContext
 */

import { useSimplifiedAuth } from '@/contexts/SimplifiedAuthContext';

interface UserRole {
  id: string;
  role: string;
  context?: string;
  permissions?: Record<string, any>;
}

interface CompanyData {
  id: string;
  name: string;
  logo_url?: string;
  company_identifier?: string;
  member_role: string;
}

interface ProgressiveAuthResult {
  // Core auth
  user: any;
  session: any;
  isAuthenticated: boolean;
  isAuthInitializing: boolean;
  
  // Data
  roles: UserRole[];
  companies: CompanyData[];
  selectedCompanyId: string | null;
  selectedCompany: CompanyData | null;
  
  // Loading states
  isDataLoading: boolean;
  isRolesLoading: boolean;
  isCompaniesLoading: boolean;
  
  // Access flags
  hasInternalAccess: boolean;
  hasCompanyAccess: boolean;
  hasRecruiterAccess: boolean;
  hasTalentAccess: boolean;
  hasNannyAccess: boolean;
  hasMotherAccess: boolean;
  
  // Actions
  setSelectedCompanyId: (id: string | null) => void;
  refetchData: () => void;
  
  // Convenience flags
  isReady: boolean; // Auth is complete
  isFullyLoaded: boolean; // Auth + data complete
}

export const useProgressiveAuth = (enableRealtime = false): ProgressiveAuthResult => {
  // Single source of truth - everything comes from SimplifiedAuthContext
  const auth = useSimplifiedAuth();
  
  // Remove console logging during startup to reduce noise
  
  return {
    // Core auth
    user: auth.user,
    session: auth.session,
    isAuthenticated: auth.isAuthenticated,
    isAuthInitializing: auth.isAuthInitializing,
    
    // Data (single source of truth)
    roles: auth.roles,
    companies: auth.companies,
    selectedCompanyId: auth.selectedCompanyId,
    selectedCompany: auth.selectedCompany,
    
    // Loading states
    isDataLoading: auth.isDataLoading,
    isRolesLoading: auth.isDataLoading, // Simplified - both loaded together
    isCompaniesLoading: auth.isDataLoading, // Simplified - both loaded together
    
    // Access flags (from context)
    hasInternalAccess: auth.hasInternalAccess,
    hasCompanyAccess: auth.hasCompanyAccess,
    hasRecruiterAccess: auth.hasRecruiterAccess,
    hasTalentAccess: auth.hasTalentAccess,
    hasNannyAccess: auth.hasNannyAccess,
    hasMotherAccess: auth.hasMotherAccess,
    
    // Actions
    setSelectedCompanyId: auth.setSelectedCompanyId,
    refetchData: auth.refetchData,
    
    // Convenience flags
    isReady: auth.isReady,
    isFullyLoaded: auth.isFullyLoaded,
  };
};

// Backward compatibility hooks
export const useUser = () => {
  const auth = useSimplifiedAuth(); // Direct context access for better performance
  return {
    user: auth.user,
    session: auth.session,
    isAuthInitializing: auth.isAuthInitializing,
    isAuthenticated: auth.isAuthenticated,
    isReady: auth.isReady,
  };
};

export const useRoles = () => {
  const auth = useSimplifiedAuth(); // Direct context access for better performance
  return {
    roles: auth.roles,
    isLoading: auth.isDataLoading,
    hasRole: (roleType: string) => auth.roles.some(role => role.role === roleType),
  };
};

export const useCompanies = () => {
  const auth = useSimplifiedAuth(); // Direct context access for better performance
  return {
    companies: auth.companies,
    selectedCompany: auth.selectedCompany,
    selectedCompanyId: auth.selectedCompanyId,
    setSelectedCompanyId: auth.setSelectedCompanyId,
    isLoading: auth.isDataLoading,
    isCompanyReady: !auth.isDataLoading,
  };
};

export const useAccessFlags = () => {
  const auth = useSimplifiedAuth(); // Direct context access for better performance
  return {
    hasInternalAccess: auth.hasInternalAccess,
    hasCompanyAccess: auth.hasCompanyAccess,
    hasRecruiterAccess: auth.hasRecruiterAccess,
    hasTalentAccess: auth.hasTalentAccess,
    hasNannyAccess: auth.hasNannyAccess,
    hasMotherAccess: auth.hasMotherAccess,
  };
};